import { gql, useMutation } from "@apollo/client";
import {
  faCheckCircle,
  faClock,
  faQuestionCircle,
  faRemove,
  faTimesCircle,
  faSort,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "@mui/material";
import moment from "moment";
import { useState, useEffect, useCallback } from "react";

import { alert } from "../../common/Alert";
import { BasicButton } from "../../common/buttons/BasicButton";

const REVOKE_INVITATION = gql`
  mutation adminRevokeInvitation(
    $teamUuid: String!
    $inviteeEmail: String!
  ) {
    adminRevokeInvitation(
      teamUuid: $teamUuid
      inviteeEmail: $inviteeEmail
    ) {
      ok
    }
  }
`;

const RESEND_INVITATION = gql`
  mutation adminResendInvitation(
    $teamUuid: String!
    $inviteeEmail: String!
    $createdAt: DateTime!
  ) {
    adminResendInvitation(
      teamUuid: $teamUuid
      inviteeEmail: $inviteeEmail
      createdAt: $createdAt
    ) {
      ok
    }
  }
`;

export function EditInvitedTeamMembers({ teamUuid, team, refetch }) {
  const [sortedInvites, setSortedInvites] = useState(
    team?.teamInvites || [],
  );
  const [sortColumn, setSortColumn] = useState(null);
  const [isSortReverse, setIsSortReverse] = useState(false);

  const sortInvites = useCallback(
    (sortBy, skipSetState = false) => {
      setSortedInvites(() => {
        let newReverse = isSortReverse;
        const allInvites = team?.teamInvites || [];

        if (!skipSetState) {
          newReverse = sortColumn === sortBy ? !isSortReverse : false;
          setIsSortReverse(newReverse);
          setSortColumn(sortBy);
        }

        return [...allInvites].sort((a, b) => {
          const aValue = a[sortBy] ?? "";
          const bValue = b[sortBy] ?? "";

          if (typeof aValue === "number") {
            return newReverse ? bValue - aValue : aValue - bValue;
          } else if (
            moment(aValue, moment.ISO_8601, true).isValid()
          ) {
            return newReverse
              ? moment(bValue).diff(moment(aValue))
              : moment(aValue).diff(moment(bValue));
          } else {
            return newReverse
              ? String(bValue).localeCompare(String(aValue))
              : String(aValue).localeCompare(String(bValue));
          }
        });
      });
    },
    [sortColumn, isSortReverse, team?.teamInvites],
  );

  useEffect(() => {
    sortInvites(sortColumn, true);
  }, [team?.teamInvites]);

  function renderIcon(inviteeStatus) {
    switch (inviteeStatus) {
      case "accepted":
        return (
          <FontAwesomeIcon
            icon={faCheckCircle}
            className="text-green-400"
          />
        );
      case "pending":
        return (
          <FontAwesomeIcon
            icon={faClock}
            className="text-yellow-400"
          />
        );
      case "revoked":
        return (
          <FontAwesomeIcon
            icon={faTimesCircle}
            className="text-red-400"
          />
        );
      case "rejected":
        return (
          <FontAwesomeIcon icon={faRemove} className="text-red-400" />
        );
      default:
        return (
          <Tooltip
            arrow
            placement="top"
            title="This invite has expired without being responded to."
          >
            <FontAwesomeIcon
              icon={faQuestionCircle}
              className="text-gray-400"
            />
          </Tooltip>
        );
    }
  }

  return (
    <div className="bg-gray-100/70 dark:bg-gray-700/30 border border-gray-50 dark:border-gray-700/50 rounded-lg py-2 px-2 w-full my-4">
      <table className="table-auto w-full dark:text-gray-300 text-left">
        <thead>
          <tr className="border-b dark:border-gray-700/50">
            <SortableHeader
              title="Invitee Email"
              sortBy="inviteeEmail"
              sortInvites={sortInvites}
            />
            <SortableHeader
              title="Invite Status"
              sortBy="status"
              sortInvites={sortInvites}
            />
            <SortableHeader
              title="Time Sent"
              sortBy="timeSent"
              sortInvites={sortInvites}
            />
            <SortableHeader
              title="Time Responded"
              sortBy="timeResponded"
              sortInvites={sortInvites}
            />
            <th></th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {sortedInvites.map((invite) => (
            <tr key={invite.uuid} className="h-10">
              <td>{invite.inviteeEmail}</td>
              <td>
                {renderIcon(invite.status)}{" "}
                {invite.status.charAt(0).toUpperCase() +
                  invite.status.slice(1)}
              </td>
              <td>
                {moment(invite.timeSent).format("MMM. DD, h:mm a")}
              </td>
              <td>
                {invite.timeResponded
                  ? moment(invite.timeResponded).format(
                      "MMM. DD, h:mm a",
                    )
                  : "-"}
              </td>
              <td className="flex py-2 gap-2">
                <ResendInviteButton
                  invite={invite}
                  teamUuid={teamUuid}
                  refetch={refetch}
                />
                <RevokeInviteButton
                  invite={invite}
                  teamUuid={teamUuid}
                  refetch={refetch}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SortableHeader({ title, sortBy, sortInvites }) {
  return (
    <th
      className="font-semibold text-sm cursor-pointer"
      onClick={() => sortInvites(sortBy)}
    >
      {title}{" "}
      <FontAwesomeIcon icon={faSort} size="xs" className="ml-1" />
    </th>
  );
}

function ResendInviteButton({ invite, refetch, teamUuid }) {
  const [resendInvitation] = useMutation(RESEND_INVITATION);
  const isRevoked = invite?.status === "revoked";
  const isAccepted = invite?.status === "accepted";

  if (isRevoked || isAccepted) return <div className="h-10"></div>;

  return (
    <BasicButton
      importance="secondary"
      size="sm"
      onClick={() => {
        resendInvitation({
          variables: {
            teamUuid,
            inviteeEmail: invite?.inviteeEmail,
            createdAt: new Date(),
          },
        })
          .then(() => {
            alert("success", "Invite sent");
            refetch();
          })
          .catch((err) => {
            console.log(err);
            refetch();
          });
      }}
    >
      Resend Invite
    </BasicButton>
  );
}

function RevokeInviteButton({ invite, teamUuid, refetch }) {
  const [revokeInvitation] = useMutation(REVOKE_INVITATION);
  const isRevoked = invite?.status === "revoked";
  const isAccepted = invite?.status === "accepted";
  const isExpired = invite?.status === "expired";

  if (isRevoked || isAccepted || isExpired)
    return <div className="h-10"></div>;

  return (
    <button
      className="border-red-500 disabled:bg-gray-400 disabled:border-gray-400 disabled:text-white disabled:dark:text-white text-red-500 hover:text-white hover:bg-red-500 px-4 py-2 text-sm shadow-sm flex-grow-0 flex flex-row items-center self-center border rounded-full font-medium outline-none transition-all"
      onClick={() => {
        revokeInvitation({
          variables: {
            teamUuid,
            inviteeEmail: invite?.inviteeEmail,
          },
        })
          .then(() => {
            alert("success", "Invite revoked.");
            refetch();
          })
          .catch((err) => {
            console.log(err);
            refetch();
          });
      }}
    >
      Revoke Invite
    </button>
  );
}
